import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import { useAuth } from '@/lib/auth'
import { UserNav } from './UserNav'

export interface NavLink {
  href: string
  label: string
  isExternal?: boolean
}

export interface PublicHeaderProps {
  navLinks?: NavLink[]
  ctaText?: string
  ctaHref?: string
}

const defaultLinks: NavLink[] = [
  { href: '/process', label: 'Process' },
  { href: '/security', label: 'Security' },
  { href: '/agentic', label: 'Agentic' },
  { href: '/info', label: 'About' },
  { href: '/join-developer', label: 'For Devs' },
]

export function PublicHeader({
  navLinks = defaultLinks,
  ctaText = 'Get Started',
  ctaHref = '/signup',
}: PublicHeaderProps) {
  const { isAuthenticated } = useAuth()
  const { scrollY } = useScroll()
  const borderOpacity = useTransform(scrollY, [0, 50], [0, 0.6])

  return (
    <motion.nav
      className="sticky top-0 z-50 bg-background/[0.88] backdrop-blur-[20px] backdrop-saturate-[1.2]"
      style={{ borderBottomColor: useTransform(borderOpacity, (v) => `rgba(226,222,214,${v})`) }}
    >
      <div className="border-b" style={{ borderColor: 'inherit' }}>
        <div className="mx-auto flex max-w-[1120px] items-center justify-between px-8 py-4">
          <div className="relative flex flex-col gap-0 items-start">
            <Link to="/" className="flex items-center">
              <img src="/logo.png" alt="Masonyt" className="h-7" />
            </Link>
            <span className="absolute -bottom-2 mb-[0.10rem] hidden text-muted-foreground/60 md:flex flex-row font-bold text-[0.09rem] md:text-[0.4rem] gap-[0.1rem]">
              Powered by{' '}
              <img
                src={`/landingpage/dlab-icon.png`}
                alt={'dlab'}
                className="mb-[0.10rem] h-[0.3rem] self-end object-cover"
              />
            </span>
          </div>

          <div className="hidden items-center gap-1 md:flex">
            {navLinks.map((link) =>
              link.isExternal || link.href.startsWith('#') ? (
                <a
                  key={link.label}
                  href={link.href}
                  className="rounded-[10px] px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className="rounded-[10px] px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  {link.label}
                </Link>
              )
            )}

            {isAuthenticated ? (
              <div className="ml-2">
                <UserNav />
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="rounded-[10px] px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  Log In
                </Link>
                <motion.div
                  whileHover={{
                    y: -1,
                    boxShadow: '0 4px 20px rgba(232,152,90,0.3)',
                  }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                  className="ml-2 inline-flex rounded-full bg-accent px-7 py-2.5 text-sm font-bold text-accent-foreground shadow-[0_2px_12px_rgba(232,152,90,0.2)] transition-colors"
                >
                  <Link to={ctaHref}>{ctaText}</Link>
                </motion.div>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.nav>
  )
}
